import { lazy, Suspense, type ReactElement } from 'react';
import { BrowserRouter, Navigate, Outlet, Route, Routes, useLocation, useNavigate } from 'react-router-dom';

import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import OrdersPage from './pages/OrdersPage';
import TapPagePortalPage from './pages/TapPagePortalPage';
import CardDesignPortalPage from './pages/CardDesignPortalPage';
import PlanBillingPage from './pages/PlanBillingPage';
import SettingsPage from './pages/SettingsPage';
import AnalyticsPage from './pages/AnalyticsPage';
import ActivateCardPage from './pages/ActivateCardPage';
import ContactsPage from './pages/ContactsPage';
import OrganizationInvitationPage from './pages/OrganizationInvitationPage';
import { API_BASE_URL } from './lib/api';
import { SessionProvider, useSession } from './session';
import './styles.css';
import './portal.css';
import './customer-dashboard-refresh.css';
import { TapBuilder } from './features/tap-builder/TapBuilder';
import { SiteHeader } from './components/SiteHeader';
import { AdminLoadingScreen } from './admin/ui/AdminLoadingScreen';
import { RouteDocumentTitle } from './components/RouteDocumentTitle';
import { useSignOut } from './useSignOut';

const AdminApp = lazy(() => import('./admin/AdminApp'));
const CardDesignerPage = lazy(() => import('./pages/CardDesignerPage'));
const StorageUsagePage = lazy(() => import('./pages/StorageUsagePage'));
const OrganizationTeamPage = lazy(() => import('./pages/OrganizationTeamPage'));
const OrganizationInvitationsPage = lazy(() => import('./pages/OrganizationInvitationsPage'));

function ProtectedRoute({ children }: { children: ReactElement }) {
  const { session } = useSession();
  if (!session) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

function AppShell() {
  const handleSignOut = useSignOut();
  const { session } = useSession();
  const { pathname } = useLocation();
  const refreshed = ['/dashboard', '/tap', '/builder', '/card-design', '/cards/design', '/card-builder', '/orders', '/analytics', '/contacts', '/plan-billing', '/team', '/team/invitations', '/settings', '/storage'].includes(pathname) || (pathname === '/activate' && Boolean(session));

  return (
    <div className={`app-shell${refreshed ? ' app-shell--customer-refresh' : ''}`}>
      <a className="skip-link" href="#main-content">
        Skip to content
      </a>
      <SiteHeader onSignOut={handleSignOut} />
      <main id="main-content" className="app-shell__main" tabIndex={-1}>
        <Outlet />
      </main>
    </div>
  );
}

function BuilderScreen() {
  const { session } = useSession();
  const navigate = useNavigate();

  if (!session) {
    return <Navigate to="/login" replace />;
  }

  if (!session.slug) {
    return (
      <section className="portal-page portal-empty-state">
        <p className="portal-eyebrow">Workspace tap page</p>
        <h1>No tap page is assigned to you here</h1>
        <p>
          This organization workspace is active, but your membership does not have a tap page yet. An organization
          owner or manager can assign one when team management is enabled.
        </p>
        <button type="button" className="portal-button portal-button--secondary" onClick={() => navigate('/dashboard')}>
          Back to workspace home
        </button>
      </section>
    );
  }

  return (
    <div className="tap-builder-screen">
      <TapBuilder
        onBackToTapPage={() => navigate('/tap')}
        initialSlug={session.slug}
        initialPublicSlug={session.publicSlug}
        apiBaseUrl={API_BASE_URL}
      />
    </div>
  );
}

function RouteFallback() {
  return (
    <section className="dashboard-section" aria-live="polite">
      <p className="helper-text">Loading...</p>
    </section>
  );
}

function LazyCardDesignerRoute() {
  return (
    <Suspense fallback={<RouteFallback />}>
      <CardDesignerPage />
    </Suspense>
  );
}

function App() {
  return (
    <SessionProvider>
      <BrowserRouter>
        <RouteDocumentTitle />
        <Routes>
          <Route
            path="/admin/*"
            element={
              <Suspense fallback={<AdminLoadingScreen />}>
                <AdminApp />
              </Suspense>
            }
          />
          <Route element={<AppShell />}>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/create-account" element={<LoginPage variant="create-account" />} />
            <Route path="/activate" element={<ActivateCardPage />} />
            <Route path="/organization-invite" element={<OrganizationInvitationPage />} />
            <Route path="/cards/design" element={<LazyCardDesignerRoute />} />
            <Route path="/card-builder" element={<LazyCardDesignerRoute />} />
            <Route path="/" element={<ProtectedRoute><Navigate to="/dashboard" replace /></ProtectedRoute>} />
            <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
            <Route path="/tap" element={<ProtectedRoute><TapPagePortalPage /></ProtectedRoute>} />
            <Route path="/card-design" element={<ProtectedRoute><CardDesignPortalPage /></ProtectedRoute>} />
            <Route path="/orders" element={<ProtectedRoute><OrdersPage /></ProtectedRoute>} />
            <Route path="/plan-billing" element={<ProtectedRoute><PlanBillingPage /></ProtectedRoute>} />
            <Route path="/storage" element={<ProtectedRoute><Suspense fallback={<RouteFallback />}><StorageUsagePage /></Suspense></ProtectedRoute>} />
            <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
            <Route path="/analytics" element={<ProtectedRoute><AnalyticsPage /></ProtectedRoute>} />
            <Route path="/contacts" element={<ProtectedRoute><ContactsPage /></ProtectedRoute>} />
            <Route path="/team" element={<ProtectedRoute><Suspense fallback={<RouteFallback />}><OrganizationTeamPage /></Suspense></ProtectedRoute>} />
            <Route path="/team/invitations" element={<ProtectedRoute><Suspense fallback={<RouteFallback />}><OrganizationInvitationsPage /></Suspense></ProtectedRoute>} />
            <Route path="/builder" element={<ProtectedRoute><BuilderScreen /></ProtectedRoute>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Route>
        </Routes>
      </BrowserRouter>
    </SessionProvider>
  );
}

export default App;
